import { useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Search, ShieldCheck, ArrowUpRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const NYSCEF = "https://iapps.courts.state.ny.us/nyscef/CaseSearch";

const COUNTIES = [
  "Statewide", "Albany", "Allegany", "Bronx", "Broome", "Cattaraugus", "Cayuga",
  "Chautauqua", "Chemung", "Chenango", "Clinton", "Columbia", "Cortland",
  "Delaware", "Dutchess", "Erie", "Essex", "Franklin", "Fulton", "Genesee",
  "Greene", "Hamilton", "Herkimer", "Jefferson", "Kings", "Lewis",
  "Livingston", "Madison", "Monroe", "Montgomery", "Nassau", "New York",
  "Niagara", "Oneida", "Onondaga", "Ontario", "Orange", "Orleans", "Oswego",
  "Otsego", "Putnam", "Queens", "Rensselaer", "Richmond", "Rockland",
  "Saratoga", "Schenectady", "Schoharie", "Schuyler", "Seneca",
  "St. Lawrence", "Steuben", "Suffolk", "Sullivan", "Tioga", "Tompkins",
  "Ulster", "Warren", "Washington", "Wayne", "Westchester", "Wyoming", "Yates",
];

const CASE_TYPES = [
  "Consumer Credit (Card) Debt Buyer Plaintiff",
  "Consumer Credit (Card) Original Creditor Plaintiff",
  "Consumer Credit (Card) Secondary Debt Buyer Plaintiff",
  "Consumer Credit (Non-Card) Transaction",
  "Consumer Debt - Other-OOM",
  "Other Matters - CSEB Confession of Judgment",
  "Post Judgment CM",
  "Post Judgment UM",
  "Contract",
  "Commercial",
  "Civil Action - General",
  "Real Property - Mortgage Foreclosure - Residential",
  "Foreclosure (non-residential mortgage)",
  "Landlord and Tenant - Nonpayment",
  "Landlord and Tenant - Holdover",
  "Mechanic's Lien",
  "Article 75 Arbitration",
  "Civil Action - Matrimonial",
];

const fmtDate = (v) => {
  if (!v) return "";
  const [y, m, d] = v.split("-");
  return `${m}/${d}/${y}`;
};

const Label = ({ children }) => (
  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-smoke">
    {children}
  </label>
);

const inputCls =
  "w-full rounded-md border border-black/10 bg-white px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/30 outline-none transition-colors duration-200 focus:border-crimson focus:ring-2 focus:ring-crimson/15";

export default function SearchConsole() {
  const [mode, setMode] = useState("party");
  const [entity, setEntity] = useState("person");
  const [nameType, setNameType] = useState("Party");
  const [first, setFirst] = useState("");
  const [middle, setMiddle] = useState("");
  const [last, setLast] = useState("");
  const [business, setBusiness] = useState("");
  const [caseId, setCaseId] = useState("");
  const [county, setCounty] = useState("Statewide");
  const [caseType, setCaseType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [narrow, setNarrow] = useState(false);
  const [error, setError] = useState("");

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [3, -3]), { stiffness: 140, damping: 18 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-3, 3]), { stiffness: 140, damping: 18 });

  const handleSearch = () => {
    setError("");
    let payload = "";
    let tab = "name";
    if (mode === "case") {
      if (!caseId.trim()) {
        setError("Enter a case identifier, e.g. 000123/2024.");
        return;
      }
      tab = "caseIdentifier";
      payload = `NYSCEF case search (DocketScout)\nCase identifier: ${caseId.trim()}`;
    } else if (entity === "business") {
      if (business.trim().length < 2) {
        setError("Business or organization name needs at least 2 characters.");
        return;
      }
      payload = `NYSCEF ${nameType.toLowerCase()} search (DocketScout)\nBusiness/Organization: ${business.trim()}`;
    } else {
      if (!first.trim() || !last.trim()) {
        setError("The court requires both a first and last name.");
        return;
      }
      payload = `NYSCEF ${nameType.toLowerCase()} search (DocketScout)\nFirst: ${first.trim()}\nMiddle: ${middle.trim()}\nLast: ${last.trim()}`;
    }
    if (mode === "party") {
      payload += `\nCounty: ${county}`;
      if (caseType) payload += `\nCase type: ${caseType}`;
      if (dateFrom || dateTo) {
        if (!dateFrom || !dateTo) {
          setError("Both filing dates are required to narrow by date.");
          return;
        }
        payload += `\nFiled: ${fmtDate(dateFrom)} - ${fmtDate(dateTo)}`;
      }
    }
    window.open(`${NYSCEF}?TAB=${tab}`, "_blank", "noopener,noreferrer");
    navigator.clipboard
      ?.writeText(payload)
      .then(() =>
        toast.success("Court search opened", {
          description: "Your search details were copied to the clipboard — paste them into the court form.",
        })
      )
      .catch(() =>
        toast("Court search opened", {
          description: "Clipboard was blocked — copy your details manually.",
        })
      );
  };

  return (
    <div id="search-console" data-testid="search-console" className="scroll-mt-28">
      <motion.div
        style={{ rotateX: rX, rotateY: rY, transformPerspective: 1100 }}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          mx.set((e.clientX - r.left) / r.width - 0.5);
          my.set((e.clientY - r.top) / r.height - 0.5);
        }}
        onMouseLeave={() => {
          mx.set(0);
          my.set(0);
        }}
        className="relative rounded-lg border border-hairline bg-paper p-6 shadow-[0_24px_70px_-24px_rgba(17,17,17,0.18)] md:p-8"
      >
        <div className="mb-6 flex items-center justify-between border-b border-hairline pb-4">
          <div className="flex gap-6 text-sm font-semibold">
            <button
              data-testid="mode-party-tab"
              onClick={() => { setMode("party"); setError(""); }}
              className={`pb-1 transition-colors duration-200 ${mode === "party" ? "border-b-2 border-crimson text-ink" : "text-smoke hover:text-ink"}`}
            >
              Party name
            </button>
            <button
              data-testid="mode-case-tab"
              onClick={() => { setMode("case"); setError(""); }}
              className={`pb-1 transition-colors duration-200 ${mode === "case" ? "border-b-2 border-crimson text-ink" : "text-smoke hover:text-ink"}`}
            >
              Case number
            </button>
          </div>
          <a
            href={`${NYSCEF}?TAB=name`}
            target="_blank"
            rel="noopener,noreferrer"
            data-testid="official-site-link"
            className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest text-smoke transition-colors hover:text-crimson"
          >
            Official NYSCEF
            <ArrowUpRight size={12} />
          </a>
        </div>

        {mode === "party" ? (
          <div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Searching as</Label>
                <div className="inline-flex rounded-md border border-black/10 bg-ivory p-0.5" data-testid="name-type-toggle">
                  {["Party", "Attorney"].map((t) => (
                    <button
                      key={t}
                      onClick={() => setNameType(t)}
                      data-testid={`name-type-${t.toLowerCase()}`}
                      className={`rounded px-4 py-1.5 text-xs font-semibold transition-all duration-200 ${nameType === t ? "bg-ink text-ivory shadow-sm" : "text-smoke hover:text-ink"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Name kind</Label>
                <div className="inline-flex rounded-md border border-black/10 bg-ivory p-0.5" data-testid="entity-toggle">
                  {[
                    ["person", "Person"],
                    ["business", "Business / Org"],
                  ].map(([v, t]) => (
                    <button
                      key={v}
                      onClick={() => setEntity(v)}
                      data-testid={`entity-${v}`}
                      className={`rounded px-4 py-1.5 text-xs font-semibold transition-all duration-200 ${entity === v ? "bg-ink text-ivory shadow-sm" : "text-smoke hover:text-ink"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {entity === "person" ? (
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div>
                  <Label>First</Label>
                  <input
                    data-testid="party-first-input"
                    value={first}
                    onChange={(e) => setFirst(e.target.value)}
                    placeholder="Robert"
                    className={inputCls}
                  />
                </div>
                <div>
                  <Label>Middle</Label>
                  <input
                    data-testid="party-middle-input"
                    value={middle}
                    onChange={(e) => setMiddle(e.target.value)}
                    placeholder="A."
                    className={inputCls}
                  />
                </div>
                <div>
                  <Label>Last</Label>
                  <input
                    data-testid="party-last-input"
                    value={last}
                    onChange={(e) => setLast(e.target.value)}
                    placeholder="Callahan"
                    className={inputCls}
                  />
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <Label>Business / Organization</Label>
                <input
                  data-testid="business-name-input"
                  value={business}
                  onChange={(e) => setBusiness(e.target.value)}
                  placeholder="Empire Advance Funding LLC"
                  className={inputCls}
                />
              </div>
            )}

            <button
              onClick={() => setNarrow(!narrow)}
              data-testid="narrow-results-toggle"
              className="mt-4 flex w-full items-center justify-between rounded-md border border-black/10 bg-ivory/60 px-3.5 py-2.5 text-sm font-semibold text-ink/75 transition-colors duration-200 hover:border-crimson/40"
            >
              Narrow results — county, case type, filing dates
              <ChevronDown size={16} className={`transition-transform duration-300 ${narrow ? "rotate-180" : ""}`} />
            </button>

            {narrow && (
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>County</Label>
                  <div className="relative">
                    <select
                      data-testid="county-select"
                      value={county}
                      onChange={(e) => setCounty(e.target.value)}
                      className={`${inputCls} appearance-none pr-9`}
                    >
                      {COUNTIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-smoke" />
                  </div>
                </div>
                <div>
                  <Label>Case type</Label>
                  <div className="relative">
                    <select
                      data-testid="case-type-select"
                      value={caseType}
                      onChange={(e) => setCaseType(e.target.value)}
                      className={`${inputCls} appearance-none pr-9`}
                    >
                      <option value="">All case types</option>
                      {CASE_TYPES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-smoke" />
                  </div>
                </div>
                <div>
                  <Label>Filing start</Label>
                  <input
                    type="date"
                    data-testid="filing-start-date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <Label>Filing end</Label>
                  <input
                    type="date"
                    data-testid="filing-end-date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-1">
            <Label>Case identifier</Label>
            <input
              data-testid="case-number-input"
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              placeholder="e.g. 000123/2024"
              className={`${inputCls} text-base`}
            />
          </div>
        )}

        <button
          onClick={handleSearch}
          data-testid="search-submit-button"
          className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-md bg-crimson py-3.5 text-sm font-bold tracking-wide text-ivory transition-colors duration-300 hover:bg-ink"
        >
          <Search size={16} />
          {mode === "party" ? "Search NYSCEF" : "Look up case number"}
        </button>

        {error ? (
          <p role="alert" data-testid="search-console-error" className="mt-3 text-xs font-semibold text-crimson">
            {error}
          </p>
        ) : (
          <p className="mt-3 flex items-center gap-2 text-[11px] leading-relaxed text-smoke" data-testid="search-console-hint">
            <ShieldCheck size={13} className="shrink-0 text-crimson" />
            Opens the official court search in a new tab. Your details are copied to the clipboard — paste them in. The court may ask for a quick security check.
          </p>
        )}
      </motion.div>
    </div>
  );
}
